import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { config, API_VERSION, IDENTITY_API_VERSION } from '@/lib/config';
import { storage } from '@/utils/storage';
import { AdminLoginResponse, TokenResponse } from '@/types';
import { getDashboardTokenUserId, isDashboardTokenUsable, useAuthStore } from '@/store/authStore';
import i18n from '@/i18n';

const TENANT_IDENTIFIER = config.tenantIdentifier;

// Create axios instances
export const apiClient: AxiosInstance = axios.create({
  baseURL: `${config.apiBaseUrl}/api/${API_VERSION}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': 'ar',
  },
});

export const identityClient: AxiosInstance = axios.create({
  baseURL: `${config.identityApiUrl}/api/${IDENTITY_API_VERSION}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': 'ar',
  },
});

export const storageClient: AxiosInstance = axios.create({
  baseURL: config.storageApiUrl,
  timeout: 60000,
  headers: {
    'Content-Type': 'multipart/form-data',
  },
});

// Request interceptor to add auth token and impersonation header
const addAuthToken = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  if (config.headers) {
    config.headers['Accept-Language'] = i18n.resolvedLanguage || i18n.language || 'ar';
  }
  const token = storage.getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (config.headers) {
    // The unsigned tenant identifier is only valid for anonymous bootstrap and
    // discovery endpoints. Authenticated tenant context comes from the signed
    // JWT (or X-Impersonate-Tenant for SuperAdmin). Sending X-Tenant-Host with
    // authenticated business/admin requests is deliberately rejected by the
    // tenant-resolution middleware.
    config.headers['X-Tenant-Host'] = TENANT_IDENTIFIER;
  }

  // Add impersonation header for SuperAdmin tenant switching
  const stored = localStorage.getItem('heglo_user');
  if (stored) {
    try {
      const state = JSON.parse(stored)?.state;
      if (state?.impersonatedTenantId && config.headers) {
        config.headers['X-Impersonate-Tenant'] = String(state.impersonatedTenantId);
      }
    } catch {
      // ignore parse errors
    }
  }

  return config;
};

// Apply auth interceptor to all clients
[apiClient, identityClient, storageClient].forEach((client) => {
  client.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));
});

// Refresh has no response interceptor: an invalid refresh must never queue
// behind itself. One promise also prevents concurrent requests rotating the
// same refresh token more than once.
const refreshClient = axios.create({ baseURL: identityClient.defaults.baseURL, timeout: 30000 });
refreshClient.interceptors.request.use(addAuthToken);
let refreshPromise: Promise<string> | null = null;

export function refreshDashboardSession(): Promise<string> {
  if (refreshPromise) return refreshPromise;
  const refreshToken = storage.getRefreshToken();
  if (!refreshToken) return Promise.reject(new Error('No refresh token'));
  const authMethod = storage.getAuthMethod();
  refreshPromise = (async () => {
    try {
      if (authMethod === 'otp') {
        const response = await refreshClient.post<TokenResponse>('/auth', { GrantType: 'refresh_token', RefreshToken: refreshToken });
        if (storage.getRefreshToken() !== refreshToken) throw new Error('Session changed');
        const { tokenString, refreshToken: nextRefreshToken } = response.data;
        if (!isDashboardTokenUsable(tokenString) || !nextRefreshToken) throw new Error('Invalid refreshed session');
        const auth = useAuthStore.getState();
        if (!auth.user?.userId || getDashboardTokenUserId(tokenString) !== auth.user.userId) throw new Error('Session identity changed');
        auth.login(tokenString, nextRefreshToken, auth.user);
        return tokenString;
      }
      const response = await refreshClient.post<AdminLoginResponse>('/admin/auth/refresh', { refreshToken });
      // A logout or a different login while this request was in flight wins.
      if (storage.getRefreshToken() !== refreshToken) throw new Error('Session changed');
      const { tokenString, refreshToken: nextRefreshToken, user } = response.data;
      if (!isDashboardTokenUsable(tokenString) || !nextRefreshToken || !user?.id)
        throw new Error('Invalid refreshed session');
      const auth = useAuthStore.getState();
      if (auth.user && auth.user.userId !== user.id) throw new Error('Session identity changed');
      auth.login(tokenString, nextRefreshToken, { ...user, userId: user.id });
      return tokenString;
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (storage.getRefreshToken() === refreshToken && (status === 400 || status === 401 || status === 403)) {
        useAuthStore.getState().logout();
      }
      throw error;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

const handleTokenRefresh = async (originalRequest: InternalAxiosRequestConfig) => {
  const token = await refreshDashboardSession();
  if (originalRequest.headers) originalRequest.headers.Authorization = `Bearer ${token}`;
  return axios(originalRequest);
};

// Add response interceptor for error handling and token refresh
[apiClient, identityClient].forEach((client) => {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      const hasAuthenticatedSession = Boolean(storage.getAccessToken() && storage.getRefreshToken());
      const isSignInRequest = /\/(?:admin\/auth\/(?:login|refresh)|auth|accounts(?:\/force_otp)?)\/?$/.test(originalRequest?.url || '');

      // A 401 from a public sign-in/OTP request is an authentication result,
      // not an expired dashboard session. Trying to refresh without a stored
      // session redirects the user away from the admin-login form and hides
      // the useful API error. Refresh only requests made by an authenticated
      // browser session.
      if (error.response?.status === 401 && hasAuthenticatedSession && !isSignInRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        return handleTokenRefresh(originalRequest);
      }

      // Handle 403 errors - clear impersonation if it might be the cause
      if (error.response?.status === 403) {
        const stored = localStorage.getItem('heglo_user');
        if (stored) {
          try {
            const state = JSON.parse(stored)?.state;
            if (state?.impersonatedTenantId) {
              console.warn('Clearing impersonated tenant due to 403 error');
              // Clear impersonation and retry once
              const newState = { ...JSON.parse(stored), state: { ...state, impersonatedTenantId: null } };
              localStorage.setItem('heglo_user', JSON.stringify(newState));
              // Don't auto-retry - let the page reload handle it
            }
          } catch {
            // ignore parse errors
          }
        }
      }

      // Handle other errors
      const errorMessage =
        (error.response?.data as { message?: string })?.message ||
        error.message ||
        'حدث خطأ غير متوقع';

      error.message = errorMessage;
      return Promise.reject(error);
    }
  );
});

// Helper function to build query params
export function buildQueryParams(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}
